import { Button } from "@/components/ui/button";
import {
  Card,
  CardTitle,
  CardHeader,
  CardContent,
} from "@/components/ui/library/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/library/select";
import { Badge } from "@/components/ui/others/Badges";
import {
  setIsStageBypassConfirmation,
  setPendingStageDraft,
} from "@/features/leads/stores/leadFunctionalSlice";
import {
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  AlertOctagon,
  RotateCcw,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDispatch } from "react-redux";
import StageBypassConfirmation from "../../../Modals/StageBypassConfirmation";
import useLeadPipeline from "@/features/leads/hook/useLeadPipeline";
import { useEffect, useMemo, useState } from "react";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import { usePermissions } from "@/rbac/usePermissions";
import { useStatusRbac } from "@/rbac/useStatusRbac";
import useVisibleStatuses from "@/features/stage/api/useVisibleStatuses";
import {
  useLeadAutomationFailures,
  useRetryAutomationRun,
} from "@/features/automation/api/useLeadAutomationFailures";
import { useShowOnlyNextStage } from "@/features/leads/api/useLeadFormConfig";
import { filterSequentialSubStatuses } from "@/features/leads/utils/sequentialSubStatus";

export default function StageSection({ item }: any) {
  const dispatch = useDispatch();
  const { pipeline } = useLeadPipeline();
  const { isChildActive } = useActiveDataMutation();
  const { allChildren } = useChildForm();
  const { bypassesRbacChecks, accessConfig, canStage } = usePermissions();
  // Org-wide "one stage at a time" provision (Settings → Manage → Stage
  // Progression). Applied AFTER the RBAC filter below, never instead of it.
  const showOnlyNextStage = useShowOnlyNextStage();

  // (The old `lead_management.level` edit gate is gone on purpose: the
  // server gates stage/status writes EXCLUSIVELY by stage_management /
  // status_management — `applyLeadStageMove` / `ChildTransition` — and the
  // extra client-only gate is what kept a View-level counsellor's granted
  // Status toggles dead in this card. User directive 2026-09-01.)

  // Per-status RBAC (role wizard "Status" block) — the entity's
  // current/draft status is pinned separately in the dropdown filter, so a
  // lead already sitting in a denied status still displays it read-only.
  const { isStatusDeniedByRole } = useStatusRbac();

  // (Same-stage status grants ride the plain per-status DENY-LIST — see
  // `stageStatusesGranted` below. The wizard shows a toggle ON when its
  // entry is missing, so "as displayed" IS the deny-list; requiring an
  // explicit saved block kept correctly-displayed roles locked out. User
  // directive 2026-09-01.)
  const isFrozen = item?.isFroze === true;
  const isChild = item?.modelName === "child" || isChildActive;

  const serverStageId =
    item?.stage?.id != null && item?.stage?.id !== ""
      ? String(item.stage.id)
      : item?.stageId != null && item?.stageId !== ""
        ? String(item.stageId)
        : "";
  const serverStatusId = item?.status?.id ? String(item.status.id) : "";
  const serverSubStatusId = item?.subStatus?.id
    ? String(item.subStatus.id)
    : "";

  const [draftStageId, setDraftStageId] = useState<string>(serverStageId);
  const [draftStatusId, setDraftStatusId] = useState<string>(serverStatusId);
  const [draftSubStatusId, setDraftSubStatusId] =
    useState<string>(serverSubStatusId);

  useEffect(() => {
    setDraftStageId(serverStageId);
    setDraftStatusId(serverStatusId);
    setDraftSubStatusId(serverSubStatusId);
  }, [serverStageId, serverStatusId, serverSubStatusId]);

  useEffect(() => {
    if (!draftStageId || !serverStageId) return;
    if (draftStageId === serverStageId) {
      setDraftStatusId(serverStatusId);
      setDraftSubStatusId(serverSubStatusId);
    }
  }, [draftStageId, serverStageId, serverStatusId, serverSubStatusId]);

  const selectedStage = useMemo(
    () => pipeline?.find((s: any) => String(s.id) === draftStageId),
    [pipeline, draftStageId]
  );

  const selectedStatus = useMemo(() => {
    const statuses =
      (selectedStage as any)?.statuses || (selectedStage as any)?.statusList;
    return statuses?.find?.((st: any) => String(st.id) === draftStatusId);
  }, [selectedStage, draftStatusId]);

  // "Sequence wise" statuses offer their sub-statuses one step at a time:
  // entering the status → its first sub-status only; within it → up to the
  // one right after the current. A process rule, so admins get it too.
  const subStatusOptions = useMemo(
    () =>
      filterSequentialSubStatuses(
        selectedStatus?.subStatuses,
        selectedStatus?.isSequential,
        {
          entering: draftStatusId !== serverStatusId,
          currentSubStatusId: serverSubStatusId,
          pinnedIds: [serverSubStatusId, draftSubStatusId],
        },
      ),
    [
      selectedStatus,
      draftStatusId,
      serverStatusId,
      serverSubStatusId,
      draftSubStatusId,
    ],
  );

  // Visibility filter — hides statuses whose configured StatusPreferences
  // don't match the current lead/child's custom-field values. Fail-open:
  // while the request is in flight, on error, or for statuses the resolver
  // didn't return, every status stays visible.
  const visibilityEntityType = isChild ? "child" : "lead";
  const visibilityEntityId =
    item?.id != null && item?.id !== "" ? Number(item.id) : null;

  // Failure banner scope — automations fire per-child for sub-status
  // changes, so surface failures for the row in view, not lead-wide. A
  // child/deal view shows only that deal's failures; a lead view shows
  // only lead-level failures. `failuresLeadId` is still resolved to the
  // parent lead id because the route is mounted under `/leads/:id` —
  // the entity scope is then narrowed via the query params.
  const failuresLeadId = useMemo(() => {
    if (isChild) {
      const v = item?.leadId;
      return v != null && v !== "" ? Number(v) : null;
    }
    const v = item?.id;
    return v != null && v !== "" ? Number(v) : null;
  }, [isChild, item?.id, item?.leadId]);

  const failuresEntityScope = useMemo(
    () => ({
      entityType: (isChild ? "child" : "lead") as "lead" | "child",
      entityId:
        item?.id != null && item?.id !== "" ? Number(item.id) : null,
    }),
    [isChild, item?.id],
  );

  const { data: automationFailures = [] } = useLeadAutomationFailures(
    failuresLeadId,
    failuresEntityScope,
  );
  const retryRun = useRetryAutomationRun(failuresLeadId);
  const visibilityStageId = draftStageId ? Number(draftStageId) : null;
  const { isVisible: isStatusVisible } = useVisibleStatuses({
    entityType: visibilityEntityType,
    entityId: visibilityEntityId,
    stageId: visibilityStageId,
  });

  const activeDealsInFutureStages = useMemo(() => {
    if (!isChild || !allChildren?.length || !pipeline?.length || !item?.stage)
      return [];

    const currentOrder =
      item.stage.order ??
      pipeline.find((s: any) => s.id === item.stage.id)?.order;

    if (currentOrder == null) return [];

    // Resolve `isRejection` per status from the pipeline (authoritative). A
    // deal whose status is a rejection is NOT "active" — it's resolved/dead —
    // so it must never raise the "active deals exist in later stages" warning.
    // Without this, a lead whose only later-stage deals are all rejected
    // (e.g. Deposit · Deposit Cancel) still showed the warning.
    const rejectionByStatusId = new Map<number, boolean>();
    const rejectionBySubStatusId = new Map<number, boolean>();
    for (const stg of pipeline as any[]) {
      const statuses = stg?.statuses || stg?.statusList || [];
      for (const st of statuses) {
        rejectionByStatusId.set(Number(st.id), !!st.isRejection);
        for (const ss of st?.subStatuses || []) {
          rejectionBySubStatusId.set(Number(ss.id), !!ss.isRejection);
        }
      }
    }
    const isDealRejected = (c: any): boolean => {
      // Two-level rejection: rejected when the STATUS is flagged OR the
      // SUB-STATUS is (SubStatus.isRejection) — mirrors rejection.where.ts.
      const subStatusId = c?.subStatus?.id ?? c?.subStatusId;
      if (
        (typeof c?.subStatus?.isRejection === "boolean" &&
          c.subStatus.isRejection) ||
        (subStatusId != null &&
          rejectionBySubStatusId.get(Number(subStatusId)) === true)
      ) {
        return true;
      }
      if (typeof c?.status?.isRejection === "boolean") return c.status.isRejection;
      const statusId = c?.status?.id ?? c?.statusId;
      if (statusId == null) return false;
      return rejectionByStatusId.get(Number(statusId)) === true;
    };

    return allChildren.filter((c: any) => {
      if (c.id === item.id) return false;
      if (c.isFroze) return false;
      if (isDealRejected(c)) return false;
      const childOrder =
        c.stage?.order ??
        pipeline.find((s: any) => s.id === (c.stageId || c.stage?.id))?.order;
      return childOrder != null && childOrder > currentOrder;
    });
  }, [isChild, allChildren, pipeline, item]);

  const handleUpdateStatus = () => {
    if (updateDisabled) return;
    dispatch(setIsStageBypassConfirmation(true));
  };

  const handlePreviousStage = () => {
    if (previousDisabled) return;
    if (!pipeline?.length || !draftStageId) return;

    const allowedIds = new Set(
      stagesForLeadPicker.map((s: any) => Number(s.id))
    );

    const currentStage = pipeline.find(
      (s: any) => String(s.id) === draftStageId
    );
    if (!currentStage) return;

    const previousStage = pipeline
      .filter(
        (s: any) =>
          s.order < currentStage.order && allowedIds.has(Number(s.id))
      )
      .sort((a: any, b: any) => b.order - a.order)[0];

    if (!previousStage) return;

    const statuses = previousStage.statuses || previousStage.statusList || [];
    // Skip statuses the role's "Status" block denies; fall back to the raw
    // list so the draft is never empty (the server still 403s a denied
    // landing).
    const selectableStatuses = statuses.filter(
      (st: any) => !isStatusDeniedByRole(Number(previousStage.id), st.id)
    );
    const pickFrom = selectableStatuses.length ? selectableStatuses : statuses;
    const lastStatus = pickFrom[pickFrom.length - 1];
    // A "Sequence wise" status is always entered at its first sub-status —
    // the picker offers nothing else there.
    const lastSubStatus =
      lastStatus?.subStatuses?.length > 0
        ? lastStatus.isSequential
          ? lastStatus.subStatuses[0]
          : lastStatus.subStatuses[lastStatus.subStatuses.length - 1]
        : undefined;

    setDraftStageId(String(previousStage.id));
    setDraftStatusId(lastStatus ? String(lastStatus.id) : "");
    setDraftSubStatusId(lastSubStatus ? String(lastSubStatus.id) : "");
  };

  const stageData = useMemo(() => {
    if (!pipeline?.length || !draftStageId) return null;

    const stage = pipeline.find((s: any) => String(s.id) === draftStageId);
    const statuses = stage?.statuses || stage?.statusList || [];
    const status = statuses.find((st: any) => String(st.id) === draftStatusId);
    const subStatus = status?.subStatuses?.find(
      (ss: any) => String(ss.id) === draftSubStatusId
    );

    return {
      stageId: draftStageId,
      statusId: draftStatusId,
      subStatusId: draftSubStatusId,
      stage,
      status,
      subStatus,
      serverStageId,
      serverStatusId,
      serverSubStatusId,
    };
  }, [
    pipeline,
    draftStageId,
    draftStatusId,
    draftSubStatusId,
    serverStageId,
    serverStatusId,
    serverSubStatusId,
  ]);

  /**
   * Stages listed as move targets are gated solely on the dedicated
   * `stage_management.update` toggle from the role wizard's "Stage
   * actions" block. `lead_management.level` governs visibility +
   * non-stage edits of the lead row (priority, source, CF values, etc.)
   * — it does NOT gate stage transitions. Mirrors the server-side gate
   * in `applyLeadStageMove`: every stage update (same-stage status
   * change OR move into the stage) requires `stage_management.update`
   * on the destination.
   *
   * The lead's current stage and the current draft pick are pinned as
   * read anchors so the dropdown can render the active value even when
   * the user has no update right on it (otherwise the trigger would
   * render empty with no way to recover).
   */
  const stagesForLeadPicker = useMemo(() => {
    if (!pipeline?.length) return [];

    const serverNum = serverStageId ? Number(serverStageId) : NaN;
    const draftNum = draftStageId ? Number(draftStageId) : NaN;

    const orderOf = (s: any): number | null => {
      const o = Number(s?.order);
      return Number.isFinite(o) ? o : null;
    };
    const current =
      pipeline.find((s: any) => Number(s.id) === serverNum) ??
      pipeline.find((s: any) => Number(s.id) === draftNum);
    const currentOrder = current ? orderOf(current) : null;

    // PASS 1 — RBAC. What the role may move into, plus the current stage and
    // the draft pick as read anchors.
    const rbacVisible = bypassesRbacChecks
      ? pipeline
      : pipeline.filter((s: any) => {
          const id = Number(s.id);
          if (!Number.isFinite(id)) return false;
          if (Number.isFinite(serverNum) && id === serverNum) return true;
          if (Number.isFinite(draftNum) && id === draftNum) return true;
          return canStage(id, "stage_management", "update");
        });

    // PASS 1.5 — DIRECTION gates vs the CURRENT stage, applied ALWAYS (not
    // just under the next-stage-only provision — user directive 2026-09-01:
    // "if rollback is disabled don't show the previous stages"). Mirrors the
    // server exactly: a BACKWARD target needs `rollback` on the source
    // stage, a FORWARD target needs `move` on it. NO RESCUE (same-day
    // second ruling — supersedes the morning's waiver): a source stage with
    // zero grants offers NO targets at all; the old "all denied → target
    // `update` pulls it forward" arm let a VISA-role user move Offer Letter
    // leads into Deposit. Anchors stay pinned.
    const srcRollback =
      Number.isFinite(serverNum) &&
      canStage(serverNum, "stage_management", "rollback");
    const srcMove =
      Number.isFinite(serverNum) &&
      canStage(serverNum, "stage_management", "move");
    const directionVisible =
      bypassesRbacChecks || currentOrder == null
        ? rbacVisible
        : rbacVisible.filter((s: any) => {
            const id = Number(s.id);
            if (Number.isFinite(serverNum) && id === serverNum) return true;
            if (Number.isFinite(draftNum) && id === draftNum) return true;
            const o = orderOf(s);
            if (o == null) return false;
            if (o < currentOrder) return srcRollback;
            if (o > currentOrder) return srcMove;
            return true;
          });

    if (!showOnlyNextStage) return directionVisible;

    // PASS 2 — the tenant's "one stage at a time" provision. A pure SUBTRACTION
    // on top of the passes above: it can only ever remove options, so RBAC
    // still decides what was permitted in the first place and this cannot
    // grant a stage a role was denied. It applies to super admins too — it
    // describes the process, not the privilege.
    //
    // No resolvable position (a stage with no order, an unseeded pipeline) →
    // leave the picker alone rather than strand the user with no target.
    if (currentOrder == null) return directionVisible;

    // The IMMEDIATE next stage is the smallest order above the current one —
    // computed, not `currentOrder + 1`, because stage orders can have gaps.
    let nextOrder: number | null = null;
    for (const s of directionVisible as any[]) {
      const o = orderOf(s);
      if (o == null || o <= currentOrder) continue;
      if (nextOrder == null || o < nextOrder) nextOrder = o;
    }

    return directionVisible.filter((s: any) => {
      const id = Number(s.id);
      // Always keep the anchors, or the trigger renders empty and the status
      // dropdown loses the stage it belongs to.
      if (Number.isFinite(serverNum) && id === serverNum) return true;
      if (Number.isFinite(draftNum) && id === draftNum) return true;

      const o = orderOf(s);
      if (o == null) return false;
      // BACKWARD stays available to whoever may roll back to it — pass 1.5
      // already required the source stage's `rollback` grant, so this
      // provision must not silently revoke that right (a mis-staged lead
      // still has to be correctable).
      if (o < currentOrder) return true;
      return nextOrder != null && o === nextOrder;
    });
  }, [
    pipeline,
    bypassesRbacChecks,
    serverStageId,
    draftStageId,
    canStage,
    showOnlyNextStage,
  ]);

  const hasDraftChange = useMemo(
    () =>
      Boolean(
        stageData &&
        (stageData.stageId !== stageData.serverStageId ||
          stageData.statusId !== stageData.serverStatusId ||
          stageData.subStatusId !== stageData.serverSubStatusId)
      ),
    [stageData]
  );

  /**
   * PUBLISH THE UNSAVED PICK (user directive 2026-09-23: "if i changed in
   * frontend the status, the follow-up's default timeline should be changed
   * via state… if status going to change, add Update status and followup
   * button").
   *
   * The drawer's FOLLOW-UP picker lives in another subtree (the bottom action
   * bar), so the draft travels through the store. Published only when all
   * three ids are set — the same validity `StageBypassConfirmation` demands of
   * the Update button, so whichever of the two paths the user takes, the same
   * payload is sent. Cleared the moment the draft matches the server again.
   */
  const entityKey = `${isChild ? "child" : "lead"}:${item?.id ?? ""}`;
  useEffect(() => {
    if (
      !hasDraftChange ||
      !item?.id ||
      !stageData?.stageId ||
      !stageData?.statusId ||
      !stageData?.subStatusId
    ) {
      dispatch(setPendingStageDraft(null));
      return;
    }
    dispatch(
      setPendingStageDraft({
        entityKey,
        stageId: Number(stageData.stageId),
        statusId: Number(stageData.statusId),
        subStatusId: Number(stageData.subStatusId),
        stageName: stageData.stage?.name ?? "",
        statusName: stageData.status?.name ?? "",
        subStatusName: stageData.subStatus?.name ?? "",
        isStageMove: stageData.stageId !== stageData.serverStageId,
      }),
    );
  }, [dispatch, entityKey, hasDraftChange, item?.id, stageData]);

  // A draft only exists while this card is on screen: leaving the Overview tab
  // (or closing the drawer) discards it, exactly as it always has.
  useEffect(
    () => () => {
      dispatch(setPendingStageDraft(null));
    },
    [dispatch],
  );

  const hasPreviousStageNav = useMemo(() => {
    if (!pipeline?.length || !draftStageId) return false;
    const allowedIds = new Set(
      stagesForLeadPicker.map((s: any) => Number(s.id))
    );
    const currentStage = pipeline.find(
      (s: any) => String(s.id) === draftStageId
    );
    if (!currentStage) return false;
    const previousStage = pipeline
      .filter(
        (s: any) =>
          s.order < currentStage.order && allowedIds.has(Number(s.id))
      )
      .sort((a: any, b: any) => b.order - a.order)[0];
    return !!previousStage;
  }, [pipeline, draftStageId, stagesForLeadPicker]);

  /**
   * Disabled states are UX-only here: server enforces RBAC for the actual
   * mutation. The Stage dropdown stays enabled because `stagesForLeadPicker`
   * already filters its options to stages the user may actually land on
   * (plus the current stage as a read anchor so they can see where the
   * lead is). Status / Sub-status gating mirrors the server EXACTLY — see
   * `canSetStatusHere` below: stage/status writes are gated by
   * stage_management / status_management only, never `lead_management.level`
   * (`applyLeadStageMove` / `ChildTransition`).
   */
  // Source stage = where the lead currently sits (server, not draft).
  // Status/sub-status changes happen WITHIN the source stage, so the
  // `stage_management.update` toggle is keyed on it.
  const sourceStageIdNum = serverStageId ? Number(serverStageId) : NaN;
  const canUpdateSourceStage = useMemo(() => {
    if (bypassesRbacChecks) return true;
    if (!Number.isFinite(sourceStageIdNum)) return false;
    return canStage(sourceStageIdNum, "stage_management", "update");
  }, [bypassesRbacChecks, sourceStageIdNum, canStage]);

  const canMoveOutOfSourceStage = useMemo(() => {
    if (bypassesRbacChecks) return true;
    if (!Number.isFinite(sourceStageIdNum)) return false;
    return canStage(sourceStageIdNum, "stage_management", "move");
  }, [bypassesRbacChecks, sourceStageIdNum, canStage]);

  const canRollbackFromSourceStage = useMemo(() => {
    if (bypassesRbacChecks) return true;
    if (!Number.isFinite(sourceStageIdNum)) return false;
    return canStage(sourceStageIdNum, "stage_management", "rollback");
  }, [bypassesRbacChecks, sourceStageIdNum, canStage]);

  // (The old RESCUE PATH — source stage with update/move/rollback all
  // denied → target `update` alone could pull the lead forward — was
  // REMOVED 2026-09-01, second ruling of the day: it let a VISA-role user
  // move Offer Letter leads into Deposit with zero grants on Offer Letter.
  // Forward now always needs `move` on the source, backward `rollback`;
  // the server dropped its matching waiver in applyLeadStageMove /
  // ChildTransition the same day.)

  // `stage_management.update` on the DRAFT (target) stage — the server's
  // destination gate for any stage-move draft.
  const canUpdateDraftStage = useMemo(() => {
    if (bypassesRbacChecks) return true;
    const n = draftStageId ? Number(draftStageId) : NaN;
    if (!Number.isFinite(n)) return false;
    return canStage(n, "stage_management", "update");
  }, [bypassesRbacChecks, draftStageId, canStage]);

  // Is this draft a stage move vs an in-stage status update?
  const draftIsStageMove = Boolean(
    draftStageId && serverStageId && draftStageId !== serverStageId,
  );

  // The Update button is intentionally NOT gated on stage-transition RBAC
  // (rollback / move / update permissions). It stays enabled whenever there's
  // a pending change — including a rollback to an earlier stage — so the user
  // is never blocked from attempting the save. Those permissions are still
  // enforced server-side (a disallowed transition 403s), so this only removes
  // the pre-emptive UI lock, never the actual rule.
  const updateDisabled = !hasDraftChange;
  // Backward navigation requires the source stage's `rollback` grant, FULL
  // STOP — the rescue path is forward-only (mirrors the server, user
  // directive 2026-09-01: rollback denied = previous stages off the table).
  const previousDisabled =
    !hasPreviousStageNav ||
    (!bypassesRbacChecks && !canRollbackFromSourceStage);
  // Stage dropdown exists to LEAVE the source stage — enabled only when the
  // viewer holds `move` or `rollback` on it (user directive 2026-09-01:
  // "no stage move access = disable that too"; a VISA-role user on Offer
  // Letter could still pick Deposit here via the removed rescue path).
  // With neither grant the picker would offer nothing but the pinned
  // current stage anyway, so it locks instead of opening an empty list.
  // Status/sub-status editing below keeps its own, independent gate.
  const stageSelectDisabled =
    !bypassesRbacChecks &&
    !canMoveOutOfSourceStage &&
    !canRollbackFromSourceStage;
  // Same-stage status editing unlocks through EITHER key the server accepts
  // (user directive 2026-09-01): `stage_management.update` on the stage, OR
  // an explicit per-status "allow" among the stage's statuses (the wizard's
  // Status toggles — the options list below then offers only the granted
  // ones). A stage-move draft instead rides `update` on the TARGET stage.
  // `lead_management.level` is NOT a
  // gate here — the server gates stage/status writes exclusively by
  // stage_management/status_management, and mirroring that keeps a View-
  // level counsellor with granted statuses working instead of staring at a
  // disabled dropdown the server would have accepted.
  // SAME-STAGE status/sub-status edits are governed by the Status toggles
  // ALONE (user directive 2026-09-01: "statuses enabled means the
  // counsellor can set them"): granted whenever the stage has at least one
  // status the role's deny-list doesn't block — exactly what the wizard
  // displays as ON, including toggles never explicitly saved. The server
  // mirrors this (same-stage skips `ensureUpdate` when the landing status
  // isn't denied). A stage-MOVE draft still rides `update` on the target.
  const stageStatusesGranted = useMemo(() => {
    if (draftIsStageMove) return false;
    const sid = Number(draftStageId);
    if (!Number.isFinite(sid)) return false;
    const statuses =
      (selectedStage as any)?.statuses ??
      (selectedStage as any)?.statusList ??
      [];
    return statuses.some((st: any) => !isStatusDeniedByRole(sid, st?.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftIsStageMove, draftStageId, selectedStage, accessConfig]);
  const canSetStatusHere =
    bypassesRbacChecks ||
    (draftIsStageMove
      ? canUpdateDraftStage
      : canUpdateSourceStage || stageStatusesGranted);
  // No extra options filter needed: granted = not-denied — exactly what the
  // dropdown's `isStatusDeniedByRole` filter below already leaves visible.
  const statusSelectDisabled = !selectedStage || !canSetStatusHere;
  const subStatusSelectDisabled = !selectedStatus || !canSetStatusHere;

  return (
    <Card className="border-[#e6e8e7] rounded-[12px] gap-2 py-2.5 shadow-none">
      <CardHeader className="px-3 pb-0">
        <CardTitle className="text-[14px] font-medium text-black tracking-[-0.28px] leading-normal">
          Stage/status
        </CardTitle>
      </CardHeader>

      <CardContent className="px-3 pt-0">
        <div className="flex flex-col gap-2">
          {automationFailures.length > 0 && (
            <div className="flex flex-col gap-1.5 rounded-[8px] px-2.5 py-2 bg-[#fef2f2] border border-[#fecaca]">
              <div className="flex items-center gap-1.5">
                <AlertOctagon
                  className="size-3.5 text-[#b91c1c] shrink-0"
                  strokeWidth={2}
                />
                <span className="text-[11px] font-medium text-[#7f1d1d]">
                  {automationFailures.length === 1
                    ? "1 automation failed on a recent change"
                    : `${automationFailures.length} automations failed on recent changes`}
                </span>
              </div>
              <ul className="flex flex-col gap-1">
                {automationFailures.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-start justify-between gap-2 rounded-[6px] bg-white border border-[#fee2e2] px-2 py-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] font-medium text-[#1f2937] truncate">
                        {f.automationName}
                      </p>
                      <p className="text-[10.5px] text-[#6b7280] truncate">
                        {(() => {
                          // Build a sentence the user can read at a
                          // glance: trigger + which deal/lead. Falls
                          // back gracefully if either piece is missing
                          // — never shows raw enum names like
                          // SUBSTATUS_CHANGED, never bare ids.
                          const trigger = f.triggerLabel ?? f.triggerEvent;
                          const target =
                            f.entityType === "child"
                              ? f.entityName
                                ? `Deal · ${f.entityName}`
                                : "this deal"
                              : f.entityName
                                ? `Lead · ${f.entityName}`
                                : "this lead";
                          return trigger
                            ? `${trigger} · ${target}`
                            : target;
                        })()}
                      </p>
                      {f.error && (
                        // Show the actual reason inline (was tucked
                        // away in a tooltip before — operators couldn't
                        // see "Intake month is required" without
                        // hovering). Tooltip retained so a long
                        // truncated error is still fully readable on
                        // hover.
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p className="text-[11px] text-[#b91c1c] truncate mt-0.5">
                              Reason: {f.error}
                            </p>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            className="max-w-[360px] bg-slate-900 text-white text-[11px] border-0 leading-relaxed whitespace-pre-wrap break-words"
                          >
                            {f.error}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[11px] text-[#b91c1c] hover:bg-[#fef2f2] shrink-0"
                      disabled={
                        retryRun.isPending &&
                        retryRun.variables === f.id
                      }
                      onClick={() => retryRun.mutate(f.id)}
                    >
                      <RotateCcw className="size-3 mr-1" />
                      {retryRun.isPending && retryRun.variables === f.id
                        ? "Retrying…"
                        : "Retry"}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isChild && !isFrozen && activeDealsInFutureStages.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 rounded-[8px] px-2.5 py-1.5 bg-[#fffbeb] border border-[#fde68a] cursor-default">
                  <AlertTriangle
                    className="size-3.5 text-[#d97706] shrink-0"
                    strokeWidth={2}
                  />
                  <span className="text-[11px] font-medium text-[#92400e]">
                    Active deals exist in later stages
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="max-w-[320px] bg-slate-900 text-white text-[11px] border-0 leading-relaxed"
              >
                {activeDealsInFutureStages
                  .map((c: any) => `${c.name} (${c.stage?.name || "—"})`)
                  .join(", ")}
              </TooltipContent>
            </Tooltip>
          )}

          <div>
            {/* Stacked full-width on phones: side by side, a Status like
                "Contacting" + its Rollback chip got ~110px and showed as
                "( Rollback". */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-1.5">
              <div className="flex-1 min-w-0">
                <label className="text-[11px] font-medium text-[#697571] tracking-[-0.22px] mb-0.5 block">
                  Stage
                </label>
                <Select
                  value={draftStageId}
                  disabled={stageSelectDisabled}
                  onValueChange={(val) => {
                    setDraftStageId(val);
                    if (val !== serverStageId) {
                      const newStage = pipeline?.find(
                        (s: any) => String(s.id) === val
                      );
                      const statuses =
                        newStage?.statuses || newStage?.statusList || [];
                      // Skip statuses the role's "Status" block denies; fall
                      // back to the raw first so the draft is never empty
                      // (the server still 403s a denied landing).
                      const selectable = statuses.filter(
                        (st: any) =>
                          !isStatusDeniedByRole(Number(val), st.id),
                      );
                      const firstStatus = selectable[0] ?? statuses[0];
                      const firstSub = firstStatus?.subStatuses?.[0];
                      setDraftStatusId(
                        firstStatus ? String(firstStatus.id) : ""
                      );
                      setDraftSubStatusId(firstSub ? String(firstSub.id) : "");
                    }
                  }}
                >
                  <SelectTrigger className="w-full h-8 bg-[#fcfcfc] border-[#cdd1d0] rounded-[8px] text-[13px]">
                    <SelectValue placeholder="Stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {stagesForLeadPicker.map((stage: any) => (
                      <SelectItem key={stage.id} value={String(stage.id)}>
                        {stage.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 min-w-0">
                <label className="text-[11px] font-medium text-[#697571] tracking-[-0.22px] mb-0.5 block">
                  Status
                </label>
                <Select
                  value={draftStatusId}
                  disabled={statusSelectDisabled}
                  onValueChange={(val) => {
                    setDraftStatusId(val);
                    const statuses =
                      (selectedStage as any)?.statuses ||
                      (selectedStage as any)?.statusList ||
                      [];
                    const newStatus = statuses.find(
                      (st: any) => String(st.id) === val
                    );
                    const firstSub = newStatus?.subStatuses?.[0];
                    setDraftSubStatusId(firstSub ? String(firstSub.id) : "");
                  }}
                >
                  <SelectTrigger className="w-full h-8 bg-[#fcfcfc] border-[#cdd1d0] rounded-[8px] text-[13px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {(
                      (selectedStage as any)?.statuses ||
                      (selectedStage as any)?.statusList ||
                      []
                    )
                      // Pin the lead/child's current status (server or draft)
                      // even when its preferences don't match or the role's
                      // "Status" block denies it — otherwise the user is
                      // staring at an empty trigger with no way to re-pick
                      // from history.
                      .filter(
                        (status: any) =>
                          (isStatusVisible(status.id) &&
                            !isStatusDeniedByRole(
                              Number(draftStageId),
                              status.id,
                            )) ||
                          String(status.id) === serverStatusId ||
                          String(status.id) === draftStatusId,
                      )
                      .map((status: any) => (
                        <SelectItem key={status.id} value={String(status.id)}>
                          <StatusOptionLabel
                            name={status.name}
                            isQualifying={!!status.isQualifying}
                            isRejection={!!status.isRejection}
                            isRollback={!!status.isRollback}
                          />
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 min-w-0">
                <label className="text-[11px] font-medium text-[#697571] tracking-[-0.22px] mb-0.5 block">
                  Sub-status
                </label>
                <Select
                  value={draftSubStatusId}
                  disabled={subStatusSelectDisabled}
                  onValueChange={setDraftSubStatusId}
                >
                  <SelectTrigger className="w-full h-8 bg-[#fcfcfc] border-[#cdd1d0] rounded-[8px] text-[13px]">
                    <SelectValue placeholder="Sub-status" />
                  </SelectTrigger>
                  <SelectContent>
                    {subStatusOptions.map((ss: any) => (
                      <SelectItem key={ss.id} value={String(ss.id)}>
                        <StatusOptionLabel
                          name={ss.name}
                          // Two-level rejection: a sub-status reads as
                          // Rejection when IT is flagged (SubStatus.isRejection)
                          // OR its parent Status is — same rule as
                          // rejection.where.ts on the server.
                          isQualifying={!!ss.isDefaultQualifying}
                          isRollback={!!ss.isDefaultRollback}
                          isRejection={
                            !!ss.isRejection || !!selectedStatus?.isRejection
                          }
                        />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </CardContent>

      <div className={`flex items-center justify-between px-3`}>
        <Button
          onClick={handlePreviousStage}
          variant="outline"
          disabled={previousDisabled}
          className="h-7 rounded-[8px] bg-[#f0f7f5] border-[#add7c7] hover:bg-[#e5f3ed] text-[#1e302a] text-[12px] font-medium tracking-[-0.24px] px-2.5 gap-1 disabled:opacity-50"
        >
          <ArrowLeft className="size-3.5" />
          Previous
        </Button>

        <Button
          variant="outline"
          className="h-7 rounded-[8px] text-[12px] font-medium tracking-[-0.24px] px-2.5 gap-1 bg-[var(--color-primary-gradient)] text-white border-transparent hover:opacity-90 hover:bg-none disabled:opacity-50"
          style={{ background: "var(--color-primary-gradient)" }}
          onClick={handleUpdateStatus}
          disabled={updateDisabled}
        >
          Update
          <ArrowRight className="size-3.5" />
        </Button>
      </div>

      <StageBypassConfirmation itemId={item?.id} stageData={stageData} />
    </Card>
  );
}

/**
 * Inline tag chips on Status / Sub-status dropdown rows. Visual matches
 * the Settings → Stage & Status panel exactly (same `Badge` variants
 * used in [StageRightBottomStatusBody.tsx]) so the two screens stay
 * consistent:
 *   - `qualify`   (purple + star)
 *   - `rejected`  (red)
 *   - `track`     (peach) → Rollback
 *
 * Pipeline flags come from the schema:
 *   Status.isQualifying / isRejection / isRollback
 *   SubStatus.isDefaultQualifying / isDefaultRollback / isRejection
 *
 * Rejection is TWO-LEVEL: a row reads as Rejection when its own
 * SubStatus.isRejection is set OR its parent Status.isRejection is —
 * the same OR the server's rejection.where.ts applies everywhere.
 */
function StatusOptionLabel({
  name,
  isQualifying,
  isRejection,
  isRollback,
}: {
  name: string;
  isQualifying?: boolean;
  isRejection?: boolean;
  isRollback?: boolean;
}) {
  const hasAnyFlag = isQualifying || isRejection || isRollback;
  return (
    <span className="flex w-full items-center justify-between gap-2 min-w-0">
      <span className="truncate">{name}</span>
      {hasAnyFlag && (
        <span className="flex items-center gap-1 shrink-0">
          {isRollback && <Badge variant="track">Rollback</Badge>}
          {isRejection && <Badge variant="rejected">Rejection</Badge>}
          {isQualifying && <Badge variant="qualify">Qualify</Badge>}
        </span>
      )}
    </span>
  );
}
